import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Share2, X, Twitter, Link, MessageCircle, Check, Send, Copy, Instagram } from 'lucide-react';
import { toast } from 'sonner';
import { analytics } from '../utils/analytics';

const ShareModal = ({ isOpen, onClose, topic, results }) => {
  const [copied, setCopied] = useState(false);
  
  const shareUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const shareText = results 
    ? `"${topic?.title}" - ${results.for_percentage}% FOR vs ${results.against_percentage}% AGAINST! What's your take? Vote now on Vent!`
    : `The Universe asks: "${topic?.title}" - Cast your vote on Vent!`;
  
  const encodedText = encodeURIComponent(shareText);
  const encodedUrl = encodeURIComponent(shareUrl);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText}\n\n${shareUrl}`);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      analytics.share('copy_link');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const handleCopyForStories = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText}\n\n${shareUrl}`);
      toast.success('Copied! Paste it in your Story');
      analytics.share('instagram_tiktok');
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const shareLinks = [
    {
      name: 'Twitter / X',
      icon: Twitter,
      url: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
      color: 'hover:bg-zinc-800',
      testId: 'share-twitter'
    },
    {
      name: 'WhatsApp',
      icon: MessageCircle,
      url: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
      color: 'hover:bg-green-900/30',
      testId: 'share-whatsapp'
    },
    {
      name: 'Telegram',
      icon: Send,
      url: `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`,
      color: 'hover:bg-blue-900/30',
      testId: 'share-telegram'
    },
    {
      name: 'Reddit',
      icon: () => (
        <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 0-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z" />
        </svg>
      ),
      url: `https://reddit.com/submit?url=${encodedUrl}&title=${encodedText}`,
      color: 'hover:bg-orange-900/30',
      testId: 'share-reddit'
    }
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-x-4 top-1/2 -translate-y-1/2 max-w-md mx-auto glass rounded-3xl p-6 z-50 max-h-[85vh] overflow-y-auto"
            data-testid="share-modal"
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Share2 className="w-5 h-5 text-vent-primary" />
                <h2 className="font-heading text-xl font-bold text-white">Share</h2>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                data-testid="close-share-modal"
              >
                <X className="w-5 h-5 text-zinc-400" />
              </button>
            </div>

            {/* Topic Preview */}
            <div className="bg-black/40 rounded-2xl p-4 mb-6 border border-white/10">
              <p className="text-sm text-zinc-500 mb-1">Today's Topic</p>
              <p className="text-white font-semibold">{topic?.title}</p>
              {results && (
                <div className="flex items-center gap-4 mt-3">
                  <span className="text-vent-for text-sm font-bold">{results.for_percentage}% FOR</span>
                  <span className="text-zinc-600">|</span>
                  <span className="text-vent-against text-sm font-bold">{results.against_percentage}% AGAINST</span>
                </div>
              )}
            </div>

            {/* Share Options */}
            <div className="space-y-2">
              {shareLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center gap-4 w-full p-3.5 rounded-2xl bg-white/5 ${link.color} transition-colors`}
                  data-testid={link.testId}
                >
                  {typeof link.icon === 'function' ? <link.icon /> : <link.icon className="w-5 h-5 text-white" />}
                  <span className="text-white font-medium text-sm">{link.name}</span>
                </a>
              ))}

              {/* Instagram / TikTok Stories - Copy to paste */}
              <button
                onClick={handleCopyForStories}
                className="flex items-center gap-4 w-full p-3.5 rounded-2xl bg-white/5 hover:bg-pink-900/20 transition-colors"
                data-testid="share-stories"
              >
                <Instagram className="w-5 h-5 text-white" />
                <span className="text-white font-medium text-sm">Instagram / TikTok Stories</span>
                <Copy className="w-3.5 h-3.5 text-zinc-500 ml-auto" />
              </button>
              
              {/* Copy Link */}
              <button
                onClick={handleCopyLink}
                className="flex items-center gap-4 w-full p-3.5 rounded-2xl bg-white/5 hover:bg-white/10 transition-colors"
                data-testid="share-copy-link"
              >
                {copied ? (
                  <Check className="w-5 h-5 text-vent-for" />
                ) : (
                  <Link className="w-5 h-5 text-white" />
                )}
                <span className="text-white font-medium text-sm">
                  {copied ? 'Copied!' : 'Copy Link'}
                </span>
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ShareModal;
